import { logger } from '@lusha/client-logger';
import { PLATFORM, SUB_PLATFORM } from '@lusha/source-platforms';
import { store } from '@reducers/store';
import _ from 'lodash';
import { communicator } from 'lusha-widgets';
import { reaction } from 'mobx';
import { applySnapshot, flow, getRoot, getSnapshot } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';
import { showContactsByDataPoints } from '../../../API/bulkApi';
import { acquireContacts } from '../../../API/contactAcquireAPI';
import { refreshContactData } from '../../../API/contactRefreshApi';
import { contactDataPointSearch } from '../../../API/contactSearchAPI';
import { sendClientMetric } from '../../../API/monitorAPI';
import { mapSingleSearchData } from '../../../API/search-v2-mappers';
import { saveContactToWishlist } from '../../../API/wishlistApi';
import { USE_BULK_KEY } from '../../../components/Pricing295UnlimitedExceededPopup';
import { BUTTON_TYPES } from '../../../components/ShowContactByDataPoint/constants';
import { getVersionedSelectors } from '../../../hooks/useSearchDataParser/pageListeners';
import { reportDncContactViewedClientEvent } from '../../../utils/BIEvents/dnc-event';
import reportClientEvent from '../../../utils/BIEvents/reportClientEvent';
import { SEARCH_RESULTS, SEARCH_STATUS } from '../../../utils/constants';
import { buildOnDemandShowClientMetricPayload } from '../../../utils/onDemand';
import { sumNumberOfCreditsCharge } from '../../../utils/showHelper';
import { BULK_SEARCH_PLATFORM } from '../../../views/BulkEnrich/helpers';
import { getContactWishlistData } from '../../../views/Wishlist/Utils';
import { getRevenueRangeString } from '../../Company/Actions/utils';
import { RATE_MODAL_TRIGGER_ACTIONS } from '../../UserActivity/constants';
import { SAVE_CONTACT_SUB_PLATFORMS } from '../../UserSettings/constants';
import { ACQUIRE_STATUS, OUT_OF_CREDITS_BULK_ERROR_MESSAGE, OUT_OF_CREDITS_BULK_ERROR_OBJECT } from '../constants';
import { setPlatformByParserSourcePlatform } from './utils';
const MAX_SEARCH_TRIES = 2;
let searchTries = 0;
const EMAIL_TYPE_TO_LABEL = {
    work: 'Work',
    direct: 'Direct',
    email: 'Email',
};
function getParsedData(parserResult, contact) {
    var _a, _b, _c, _d;
    const { ordinalId } = contact;
    const { parsedData } = JSON.parse(parserResult);
    if (ordinalId >= 0) {
        const contactParsedData = parsedData.contacts[ordinalId];
        return {
            parsedFullName: contactParsedData.fullName,
            parsedCompanyName: ((_b = (_a = contactParsedData.currentPositions) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.companyName) || null,
            parsedLinkedinUrl: contactParsedData.socialLink,
        };
    }
    return {
        parsedFullName: parsedData.full_name,
        parsedCompanyName: ((_d = (_c = parsedData.organizations) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.name) || parsedData.top_company || null,
        parsedLinkedinUrl: parsedData.link,
    };
}
const setEmailType = item => {
    const itemLabel = EMAIL_TYPE_TO_LABEL[item.type];
    if (itemLabel) {
        return itemLabel;
    }
    if (EMAIL_TYPE_TO_LABEL[item.label]) {
        return EMAIL_TYPE_TO_LABEL[item.label];
    }
    if (item.is_revealed && item.is_work_email === null) {
        return EMAIL_TYPE_TO_LABEL.email;
    }
    return item.is_work_email ? EMAIL_TYPE_TO_LABEL.work : EMAIL_TYPE_TO_LABEL.direct;
};
function getContactLocation(location, contactLocation) {
    if (location) {
        return location;
    }
    if (contactLocation) {
        return `${contactLocation.city ? `${contactLocation.city}, ` : ''}${contactLocation.country}`;
    }
    return '';
}
const getEmailConfidence = qualityScore => {
    if (!qualityScore) {
        return null;
    }
    if (qualityScore > 75) {
        return 'A+';
    }
    return 'A';
};
function parseEmailData(email, originalEmail) {
    var _a;
    if (!email) {
        return {};
    }
    return {
        value: email.address || email.email || '',
        type: setEmailType(email),
        isRevealed: email.is_revealed || email.isShown || email.status === 'shown',
        isEdited: email.isEdited || email.is_edited,
        isManuallyAdded: email.isManuallyAdded || email.is_manually_added,
        feedback: (_a = email.feedback) !== null && _a !== void 0 ? _a : null,
        emailConfidence: getEmailConfidence(email.quality_score),
        id: email.id || (originalEmail === null || originalEmail === void 0 ? void 0 : originalEmail.id),
        isGuessed: email.is_guessed || (originalEmail === null || originalEmail === void 0 ? void 0 : originalEmail.isGuessed),
        shownDate: email.shown_date || (originalEmail === null || originalEmail === void 0 ? void 0 : originalEmail.shown_date),
        status: email.status || (originalEmail === null || originalEmail === void 0 ? void 0 : originalEmail.status),
        shown_by: email.shown_by,
    };
}
function parsePhoneData(phone, defaultLabel, origianlPhone) {
    var _a;
    if (!phone) {
        return {};
    }
    return {
        value: phone.text || phone.phone || phone.number || '-',
        type: phone.type || defaultLabel,
        isEdited: phone.isEdited || phone.is_edited,
        isManuallyAdded: phone.isManuallyAdded,
        doNotCall: phone.do_not_call || null,
        isDncPhone: phone.is_do_not_call || false,
        feedback: (_a = phone.feedback) !== null && _a !== void 0 ? _a : null,
        id: phone.id || (origianlPhone === null || origianlPhone === void 0 ? void 0 : origianlPhone.id),
        status: phone.status || (origianlPhone === null || origianlPhone === void 0 ? void 0 : origianlPhone.status),
        shownDate: phone.shown_date || (origianlPhone === null || origianlPhone === void 0 ? void 0 : origianlPhone.shown_date),
        shownBy: phone.shown_by || (origianlPhone === null || origianlPhone === void 0 ? void 0 : origianlPhone.shown_by),
        isRevealed: phone.is_revealed || (origianlPhone === null || origianlPhone === void 0 ? void 0 : origianlPhone.is_revealed) || phone.isShown || phone.status === 'shown',
        shown_by: phone.shown_by,
    };
}
function setWishlistCompanyData(company) {
    var _a;
    // Handle both MST snapshot (camelCase) and API response (snake_case)
    const headquarter = (company === null || company === void 0 ? void 0 : company.headquarter) || (company === null || company === void 0 ? void 0 : company.location) || {};
    const { city = '', region = '', country = '' } = headquarter;
    // Handle both property name formats for company size
    const companySize = (company === null || company === void 0 ? void 0 : company.company_size) || { min: company === null || company === void 0 ? void 0 : company.min, max: company === null || company === void 0 ? void 0 : company.max };
    const min = (companySize === null || companySize === void 0 ? void 0 : companySize.min) || 0;
    const max = (companySize === null || companySize === void 0 ? void 0 : companySize.max) || 0;
    const domains = (company === null || company === void 0 ? void 0 : company.domains) || {};
    const social = (company === null || company === void 0 ? void 0 : company.social) || {};
    const industry = (company === null || company === void 0 ? void 0 : company.industry) || {};
    const revenueRange = (company === null || company === void 0 ? void 0 : company.revenue_range) || (company === null || company === void 0 ? void 0 : company.revenueRange);
    return {
        id: 'wishlist',
        name: (company === null || company === void 0 ? void 0 : company.name) || '',
        hasCompany: !!company,
        city,
        region,
        country,
        min,
        max,
        // Handle both 'about' (MST) and 'description' (API)
        about: (company === null || company === void 0 ? void 0 : company.about) || (company === null || company === void 0 ? void 0 : company.description) || '',
        // Handle both 'logoUrl' (MST) and 'logo_url' (API)
        logoUrl: (company === null || company === void 0 ? void 0 : company.logoUrl) || (company === null || company === void 0 ? void 0 : company.logo_url) || '',
        // Handle both 'domain' (MST) and 'domains.homepage' (API)
        domain: (company === null || company === void 0 ? void 0 : company.domain) || (domains === null || domains === void 0 ? void 0 : domains.homepage) || 'Company Website',
        // Handle both 'homePageUrl' (MST) and 'homepage_url' (API)
        homePageUrl: (company === null || company === void 0 ? void 0 : company.homePageUrl) || (company === null || company === void 0 ? void 0 : company.homepage_url) || '',
        founded: (company === null || company === void 0 ? void 0 : company.founded) ? company.founded.toString() : '',
        // Handle both 'specialities' (MST) and 'industry_tags' (API)
        specialities: (company === null || company === void 0 ? void 0 : company.specialities) || (company === null || company === void 0 ? void 0 : company.industry_tags) || [],
        // Handle both 'mainIndustry' (MST) and nested API structure
        mainIndustry: (company === null || company === void 0 ? void 0 : company.mainIndustry) || ((_a = industry === null || industry === void 0 ? void 0 : industry.primary_industry) === null || _a === void 0 ? void 0 : _a.key) || '',
        facebook: (company === null || company === void 0 ? void 0 : company.facebook) || (social === null || social === void 0 ? void 0 : social.facebook) || '',
        twitter: (company === null || company === void 0 ? void 0 : company.twitter) || (social === null || social === void 0 ? void 0 : social.twitter) || '',
        LinkedIn: (company === null || company === void 0 ? void 0 : company.LinkedIn) || (social === null || social === void 0 ? void 0 : social.LinkedIn) || (social === null || social === void 0 ? void 0 : social.linkedin) || '',
        companyResultString: (company === null || company === void 0 ? void 0 : company.companyResultString) || JSON.stringify(company),
        revenueRange: revenueRange
            ? Object.assign(Object.assign({}, (typeof revenueRange === 'object' ? revenueRange : {})), { string: getRevenueRangeString(revenueRange) }) : {},
        // Handle both 'companyLid' (MST) and 'company_lid' (API)
        companyLid: (company === null || company === void 0 ? void 0 : company.companyLid) || (company === null || company === void 0 ? void 0 : company.company_lid) || '',
        isBlockedForShow: (company === null || company === void 0 ? void 0 : company.isBlockedForShow) || false,
        isSocial: (company === null || company === void 0 ? void 0 : company.isSocial) || !!(company === null || company === void 0 ? void 0 : company.social),
    };
}
function buildDataPointShowRequest(self, platform, pageUrl, isUseBulk) {
    // single show
    const mappedPlatform = SAVE_CONTACT_SUB_PLATFORMS[platform] || SUB_PLATFORM.LINKEDIN_MAIN;
    return {
        request_id: self.requestId,
        mask_id: self.maskedId,
        is_onboarding: self.isOnboarding,
        contacts: self.dataPointIdsToShow,
        metadata: {
            source_product: mappedPlatform,
            source_platform: PLATFORM.PLUGIN,
            url: pageUrl,
        },
        use_bulk_credits: isUseBulk,
    };
}
const Actions = self => {
    let initialState;
    const resetState = () => {
        var _a, _b;
        const isSearchByDataPointEnabled = (_b = (_a = getRoot(self)) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.isSearchByDataPointEnabled;
        const selectedDataPointType = isSearchByDataPointEnabled ? BUTTON_TYPES.NONE : BUTTON_TYPES.EMAIL_AND_PHONE;
        applySnapshot(self, Object.assign(Object.assign({}, initialState), { selectedDataPointType }));
    };
    const onSearchStatusChange = searchStatus => {
        const { postMessage: setSearchResult } = communicator.getMessenger('SEARCH_RESULT');
        const { postMessage: openFrame } = communicator.getMessenger('OPEN');
        const { totalDataCollected } = self;
        const { userSettings, context } = getRoot(self);
        const selectors = getVersionedSelectors(userSettings.externalPluginVersion, context.contextTypeName);
        logger.info('SEARCH STATUS: ', searchStatus);
        switch (searchStatus) {
            case SEARCH_STATUS.RESOLVED: {
                const dataExist = totalDataCollected === 0 ? SEARCH_RESULTS.MAX_SEARCH_RESULT : totalDataCollected;
                const { settings } = store.getState();
                const isAutoOpenEnabled = _.isUndefined(settings.autoOpen) ? settings.autoOpenDefault : settings.autoOpen;
                const shouldOpenPlugin = (dataExist === SEARCH_RESULTS.MAX_SEARCH_RESULT || dataExist > 0) && isAutoOpenEnabled;
                setSearchResult({ isRevealed: self.isRevealed, count: dataExist });
                if (shouldOpenPlugin) {
                    openFrame({ openType: 'autoOpen' });
                }
                if (self.hasDncPhone) {
                    reportDncContactViewedClientEvent({
                        uniqueSearchId: self.uniqueSearchId,
                        subPlatform: userSettings.platform,
                        pageUrl: context.url,
                        numOfContactsWithDncPhone: 1,
                        numOfContactsWithOnlyDncPhones: self.hasOnlyDncPhones ? 1 : 0,
                        isBulkSearch: false,
                    });
                }
                context.setSessionExpired(false);
                break;
            }
            case SEARCH_STATUS.MAX_SEARCHES_REACHED:
            case SEARCH_STATUS.PARSER_ERROR:
                setSearchResult({ count: SEARCH_RESULTS.MAX_SEARCH_RESULT });
                break;
            case SEARCH_STATUS.REJECTED:
                setSearchResult({ count: -1, pageListeners: selectors[userSettings.platform] });
                break;
            case SEARCH_STATUS.RETRYING_SEARCH:
                setSearchResult({ count: 0, pageListeners: selectors[userSettings.platform] });
                break;
            default:
                break;
        }
    };
    return {
        afterCreate: () => {
            initialState = getSnapshot(self);
            reaction(() => self.searchStatus, onSearchStatusChange);
        },
        setWishlistAddedData: data => {
            self.wishlistData.emails = data.emails;
            self.wishlistData.phones = data.phones;
            self.wishlistData.email1 = data.emails[0] || null;
            self.wishlistData.email2 = data.emails[1] || null;
            self.wishlistData.phone1 = data.phones[0] || null;
            self.wishlistData.phone2 = data.phones[1] || null;
        },
        setContactEditableData: ({ emails, phones, feedback, jobTitle }) => {
            var _a, _b, _c, _d, _e, _f;
            self.email1 = parseEmailData(emails[0], self.email1);
            self.email2 = parseEmailData(emails[1], self.email2);
            self.phone1 = parsePhoneData(phones[0], 'Phone', self.phone1);
            self.phone2 = parsePhoneData(phones[1], 'Phone', self.phone2);
            if (jobTitle) {
                self.title = (_a = jobTitle.title) !== null && _a !== void 0 ? _a : '';
                self.editedFields = (_b = jobTitle.editedFields) !== null && _b !== void 0 ? _b : [];
            }
            if (feedback) {
                self.email1.feedback = (_c = feedback[self.email1.value]) !== null && _c !== void 0 ? _c : null;
                self.email2.feedback = (_d = feedback[self.email2.value]) !== null && _d !== void 0 ? _d : null;
                self.phone1.feedback = (_e = feedback[self.phone1.value]) !== null && _e !== void 0 ? _e : null;
                self.phone2.feedback = (_f = feedback[self.phone2.value]) !== null && _f !== void 0 ? _f : null;
            }
        },
        setWishlistContact: wishlistContact => {
            var _a;
            const { company } = wishlistContact;
            const defaultList = (wishlistContact === null || wishlistContact === void 0 ? void 0 : wishlistContact.listId) ? [wishlistContact.listId] : [];
            // Normalize email structure (handle both 'value' and 'address' properties)
            const normalizeEmail = email => {
                if (!email) {
                    return null;
                }
                return {
                    value: email.value || email.address || '',
                    type: email.type || 'work',
                };
            };
            // Normalize phone structure (handle 'value', 'text', and 'number' properties)
            const normalizePhone = phone => {
                if (!phone) {
                    return null;
                }
                return {
                    value: phone.value || phone.text || phone.number || '',
                    type: phone.type || 'work',
                };
            };
            const normalizedEmails = (wishlistContact.emails || []).map(normalizeEmail);
            const normalizedPhones = (wishlistContact.phones || []).map(normalizePhone);
            self.contactId = wishlistContact.id;
            self.wishlistData = {
                hasCompany: true,
                id: wishlistContact.id,
                firstName: wishlistContact.firstName || '',
                lastName: (wishlistContact === null || wishlistContact === void 0 ? void 0 : wishlistContact.lastName) || '',
                jobTitle: ((_a = wishlistContact.jobTitle) === null || _a === void 0 ? void 0 : _a.title) || wishlistContact.jobTitle || '',
                phones: normalizedPhones,
                emails: normalizedEmails,
                email1: normalizedEmails[0] || null,
                email2: normalizedEmails[1] || null,
                phone1: normalizedPhones[0] || null,
                phone2: normalizedPhones[1] || null,
                company: setWishlistCompanyData(company),
                integrations: Array.isArray(wishlistContact.integrations) ? {} : wishlistContact.integrations,
                lists: wishlistContact.contactLists || defaultList,
            };
            self.isUserContactOwner = true;
            self.tags = wishlistContact.contactTags || [];
        },
        getContactOwnerId: (emails, phones) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            const emailDate = (_a = emails === null || emails === void 0 ? void 0 : emails[0]) === null || _a === void 0 ? void 0 : _a.shownDate;
            const phoneDate = (_b = phones === null || phones === void 0 ? void 0 : phones[0]) === null || _b === void 0 ? void 0 : _b.shownDate;
            if (!emailDate) {
                return ((_d = (_c = phones === null || phones === void 0 ? void 0 : phones[0]) === null || _c === void 0 ? void 0 : _c.shown_by) === null || _d === void 0 ? void 0 : _d.user_id) || null;
            }
            if (!phoneDate) {
                return ((_f = (_e = emails === null || emails === void 0 ? void 0 : emails[0]) === null || _e === void 0 ? void 0 : _e.shown_by) === null || _f === void 0 ? void 0 : _f.user_id) || null;
            }
            return emailDate < phoneDate ? (_h = (_g = emails === null || emails === void 0 ? void 0 : emails[0]) === null || _g === void 0 ? void 0 : _g.shown_by) === null || _h === void 0 ? void 0 : _h.user_id : (_k = (_j = phones === null || phones === void 0 ? void 0 : phones[0]) === null || _j === void 0 ? void 0 : _j.shown_by) === null || _k === void 0 ? void 0 : _k.user_id;
        },
        setContact: contactData => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            const { name, emails = [], phones = [], jobTitles = [], contact, requestId, ordinalId = -1, socialLink, contactId: contactSearchId, maskedContactId, location, contactLocation, isBlockedForShow, contactSocialLinks, revealedBy, isMyContact, userId, previousJob, isRevealed, canShowContact, maskId, isPressShow, isOnDemandContactFound = false, personId, isFreshData = false, freshDataSummary = null, uniqueSearchId, isAvailableForUnshownSave, } = contactData;
            const isSearchByDataPointEnabled = (_b = (_a = getRoot(self)) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.isSearchByDataPointEnabled;
            const { showDate = '', lists = '', savedIntegrations = {}, feedback, tags } = contact || {};
            let { id: contactId = '' } = contact || {};
            // id = searchId, contactId = showId
            const [contactDepartments] = jobTitles;
            // If the acquire status is complete, the plugin behaves the same for all contacts.
            const acquireStatus = contactData.acquireStatus || ACQUIRE_STATUS.complete;
            if (((_c = contactData.wishedContact) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                const [wishlistData] = contactData.wishedContact;
                contactId = wishlistData.id;
                self.setWishlistContact(wishlistData);
            }
            else {
                self.wishlistData = null;
            }
            // check for personId (not exist in show response)
            if (personId) {
                self.personId = personId;
            }
            if (envConfig.FEATURES.freshDataEnabled) {
                self.isFreshData = isFreshData;
                self.freshDataSummary = freshDataSummary;
            }
            self.isAvailableForUnshownSave = isAvailableForUnshownSave;
            if (isAvailableForUnshownSave) {
                self.contactList = (lists === null || lists === void 0 ? void 0 : lists.length) > 0 ? lists.split(',').pop() : '';
            }
            self.isPressShow = isPressShow || false;
            self.maskedContactId = maskedContactId;
            self.maskedId = maskId || null;
            self.requestId = requestId || self.requestId;
            self.ordinalId = ordinalId;
            self.showDate = showDate || '';
            self.contactId = contactId;
            self.contactSearchId = contactSearchId;
            self.contactList = (_d = self.contactList) !== null && _d !== void 0 ? _d : (lists.length > 0 ? lists.split(',').pop() : '');
            self.fullName = name || '';
            self.isChecked = false;
            self.canShowContact = canShowContact;
            self.acquireStatus = acquireStatus;
            if (acquireStatus === ACQUIRE_STATUS.complete) {
                self.setContactEditableData({
                    emails,
                    phones,
                    feedback,
                    jobTitle: jobTitles === null || jobTitles === void 0 ? void 0 : jobTitles[0],
                    isSearchByDataPointEnabled,
                });
            }
            else {
                self.uuid = contactData.uuid;
                self.contactHash = contactData.contactHash;
            }
            self.setIntegrationsData(savedIntegrations);
            self.setSavedDatapoints(savedIntegrations);
            self.searchStringResult = JSON.stringify(Object.assign(Object.assign({}, contactData), { contactId })) || '';
            self.socialLink = socialLink || (contactSocialLinks === null || contactSocialLinks === void 0 ? void 0 : contactSocialLinks.linkedin) || '';
            self.location = getContactLocation(location, contactLocation);
            self.locationCode = (_e = contactData.contactLocation) === null || _e === void 0 ? void 0 : _e.country_iso2;
            self.department = (_h = (((_f = contactDepartments === null || contactDepartments === void 0 ? void 0 : contactDepartments.departments) === null || _f === void 0 ? void 0 : _f[0]) || ((_g = contactDepartments === null || contactDepartments === void 0 ? void 0 : contactDepartments.department) === null || _g === void 0 ? void 0 : _g[0]))) !== null && _h !== void 0 ? _h : '';
            self.seniority = (_j = contactDepartments === null || contactDepartments === void 0 ? void 0 : contactDepartments.seniority) !== null && _j !== void 0 ? _j : '';
            self.isRevealed = (!self.isOnboarding && !!self.showDate) || (!self.isOnboarding && isRevealed); // OR /* all contact data are revealed*/
            self.tags = tags || [];
            self.isBlockedForShow = isBlockedForShow || false;
            self.revealedBy = revealedBy || null;
            self.isMyContact = isMyContact || null;
            self.userId = userId || null;
            self.previousJob = previousJob;
            self.isOnDemandContactFound = isOnDemandContactFound;
            self.isUserContactOwner = true;
            if (uniqueSearchId) {
                self.uniqueSearchId = uniqueSearchId;
            }
        },
        setSearchV2Contact: contact => {
            var _a, _b;
            const { id, fullName, emails, phones, jobTitle, location, isBlockedForShow, isShown, tags, contactId, listId, socialLink, showDate, integrations, state, changes, wishlistContact, ordinalId = -1, previousJob, canShowContact, maskedContactId, isOnDemandContactFound = false, requestId, isFromDetailsFoundTab = false, maskedId = '', storeCompanyKey = null, feedback, personId = null, isFreshData = false, freshDataSummary = null, uniqueSearchId, isAvailableForUnshownSave, firstName, isPartialProfile, company = null, } = contact;
            self.setContactEditableData({ emails, phones, jobTitle, feedback });
            self.personId = personId;
            self.isPartialProfile = isPartialProfile;
            self.searchStringResult = JSON.stringify(contact);
            self.fullName = fullName !== null && fullName !== void 0 ? fullName : '';
            self.isBlockedForShow = isBlockedForShow;
            self.isRevealed = isShown;
            self.location = location === null || location === void 0 ? void 0 : location.country;
            self.seniority = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.seniority;
            self.department = ((_a = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.department) === null || _a === void 0 ? void 0 : _a[0]) || ((_b = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.departments) === null || _b === void 0 ? void 0 : _b[0]);
            self.socialLink = socialLink;
            self.ordinalId = ordinalId;
            self.previousJob = previousJob;
            self.canShowContact = canShowContact;
            self.maskedContactId = maskedContactId;
            self.isOnDemandContactFound = isFromDetailsFoundTab || isOnDemandContactFound;
            self.isFromDetailsFoundTab = isFromDetailsFoundTab;
            self.maskedId = maskedId;
            self.isAvailableForUnshownSave = isAvailableForUnshownSave;
            self.firstName = firstName;
            self.storeCompanyKey = storeCompanyKey;
            if (uniqueSearchId) {
                self.uniqueSearchId = uniqueSearchId;
            }
            if (requestId) {
                self.requestId = requestId;
            }
            if (envConfig.FEATURES.freshDataEnabled) {
                self.isFreshData = isFreshData;
                self.freshDataSummary = freshDataSummary;
            }
            if (state) {
                self.state = state;
            }
            if (isAvailableForUnshownSave) {
                self.contactList = (listId === null || listId === void 0 ? void 0 : listId.length) > 0 ? listId.split(',').pop() : '';
                self.setIntegrationsData(integrations);
            }
            self.contactId = contactId || id;
            if (isShown) {
                self.contactId = contactId || '';
                // list ids are chained with commas, e.g: "all,f32c2d18-09ec-40f4-b048-77a3549a9cb1"
                self.contactList = (listId === null || listId === void 0 ? void 0 : listId.length) > 0 ? listId.split(',').pop() : '';
                self.showDate = showDate || '';
                self.tags = tags;
                self.setIntegrationsData(integrations);
                self.setSavedDatapoints(integrations);
                if (changes === null || changes === void 0 ? void 0 : changes.length) {
                    self.changes = changes;
                }
            }
            else if (wishlistContact) {
                self.setWishlistContact(wishlistContact);
            }
            else if (listId && listId.length > 0 && isPartialProfile && !isShown) {
                self.contactList = (listId === null || listId === void 0 ? void 0 : listId.length) > 0 ? listId.split(',').pop() : '';
                self.setWishlistContact(Object.assign(Object.assign({}, contact), { id: contact.contactId, listId: self.contactList, company }));
            }
            self.isUserContactOwner = true;
        },
        resetFreshData: () => {
            self.isFreshData = false;
            self.freshDataSummary = null;
        },
        setIntegrationsData: savedIntegrations => {
            Object.entries(savedIntegrations).forEach(([key, { link, type, outdatedSince, lastUpdated, errors }]) => {
                if (!(errors === null || errors === void 0 ? void 0 : errors.length) && link) {
                    self.integrationsData.set(key, Object.assign(Object.assign({ link,
                        type }, (outdatedSince && { outdatedSince })), (lastUpdated && { lastUpdated })));
                }
            });
        },
        setIntegrationByKey: (integrationKey, { link, lastUpdated, outdatedSince }) => {
            const integration = self.integrationsData.get(integrationKey);
            if (integration) {
                integration.link = link;
                integration.lastUpdated = lastUpdated;
                integration.outdatedSince = outdatedSince;
            }
            else {
                self.integrationsData.set(integrationKey, { link, lastUpdated, outdatedSince });
            }
        },
        setSavedDatapoints: integrations => {
            Object.values(integrations).forEach(({ dataPoints }) => {
                if (!dataPoints || !dataPoints.length) {
                    return;
                }
                dataPoints.forEach(datapointId => {
                    if (!self.savedDatapoints.includes(datapointId)) {
                        self.savedDatapoints.push(datapointId);
                    }
                });
            });
        },
        refreshData: flow(function* refreshData() {
            const payload = {
                requestId: self.requestId,
                contacts: [self.contactId],
            };
            try {
                const response = yield refreshContactData(payload);
                const contactReponse = response[self.contactId];
                if (!contactReponse.isSuccess) {
                    throw new Error(`Could not refresh contact id: ${self.contactId}`);
                }
                self.setSearchV2Contact(contactReponse.contact);
            }
            catch (e) {
                logger.error('Failed to refresh contact', e);
            }
        }),
        search: flow(function* search(data, forceSearch = false) {
            var _a, _b;
            const rootStore = getRoot(self);
            const { userSettings: { isPluginOpen }, app, user, onDemand, } = rootStore;
            const { postMessage: setSearchResult } = communicator.getMessenger('SEARCH_RESULT');
            if (!self.isEmpty && !onDemand.isOnDemandContactShown) {
                logger.info('Search already retrieve result');
                self.searchStatus = SEARCH_STATUS.RESOLVED;
                setSearchResult({ count: SEARCH_RESULTS.MAX_SEARCH_RESULT });
                return;
            }
            if (searchTries >= MAX_SEARCH_TRIES && !forceSearch) {
                logger.info(`Reached ${MAX_SEARCH_TRIES} searches for current profile`);
                self.searchStatus = SEARCH_STATUS.MAX_SEARCHES_REACHED;
                return;
            }
            searchTries += 1;
            self.searchStatus = SEARCH_STATUS.SEARCHING;
            logger.info(`Initiating search try number ${searchTries} for current profile`);
            let searchResult;
            setPlatformByParserSourcePlatform(data, rootStore);
            try {
                const searchData = Object.assign({ request_id: self.requestId, firstSearch: self.firstSearch, sessionId: app.sessionId }, data);
                const mappedSearchData = mapSingleSearchData(searchData, Boolean(isPluginOpen));
                const searchContactByDataPointResponse = yield contactDataPointSearch(mappedSearchData);
                searchResult = searchContactByDataPointResponse === null || searchContactByDataPointResponse === void 0 ? void 0 : searchContactByDataPointResponse.data;
            }
            catch (e) {
                // If not maxed on search tries and error is parsing error - retry search
                if ((searchTries >= MAX_SEARCH_TRIES && !forceSearch) || e.code !== 422) {
                    self.searchStatus = SEARCH_STATUS.MAX_SEARCHES_REACHED;
                }
                else {
                    self.searchStatus = SEARCH_STATUS.RETRYING_SEARCH;
                }
                return;
            }
            if (searchResult) {
                const { isOnboarding, maskId } = searchResult.metadata;
                self.isOnboarding = isOnboarding;
                self.maskedId = maskId;
                if (isOnboarding) {
                    reportClientEvent({
                        eventType: 'user_onboarding_demo_search',
                        eventValue: user.industry,
                    }, { enrichData: false });
                }
                self.firstSearch = false;
                const contact = searchResult.contacts[0];
                const { wishlistContact, uniqueSearchId = '' } = contact;
                const company = wishlistContact
                    ? Object.assign(Object.assign({}, wishlistContact.company), { company_lids: (_b = (_a = searchResult === null || searchResult === void 0 ? void 0 : searchResult.companies) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.company_lids }) : searchResult.companies[0] || null;
                self.company.setCompany(company);
                self.setSearchV2Contact(Object.assign(Object.assign({}, contact), { maskedId: maskId, company: company !== null && company !== void 0 ? company : null }));
                self.searchStatus = SEARCH_STATUS.RESOLVED;
                onDemand.setIsOnDemandContactShown(false);
                self.uniqueSearchId = uniqueSearchId;
                app === null || app === void 0 ? void 0 : app.setUniqueSearchId(uniqueSearchId);
                return;
            }
            self.searchStatus = SEARCH_STATUS.REJECTED;
        }),
        revealContact: flow(function* revealContact() {
            const { userSettings, context, onDemand } = getRoot(self);
            if (self.isFromDetailsFoundTab && context.type === 'PROFILE') {
                //   This is for the extreme case, that the user has an on-demand contact,
                //   and the user is on that specific contact profile on linkedin.
                //   In case of show from history tab, trigger new search request, from prospect tab
                //  */
                onDemand.setIsOnDemandContactShown(true);
            }
            const isUseBulk = sessionStorage.getItem(USE_BULK_KEY);
            const showPayload = buildDataPointShowRequest(self, userSettings.platform, context.url, !!isUseBulk);
            const numberOfCreditsCharged = sumNumberOfCreditsCharge((showPayload === null || showPayload === void 0 ? void 0 : showPayload.contacts) || []);
            const showResult = yield showContactsByDataPoints(showPayload);
            const response = showResult === null || showResult === void 0 ? void 0 : showResult.data.data.contacts.request[0];
            return { showResult, response, numberOfCreditsCharged };
        }),
        show: flow(function* show() {
            var _a, _b, _c, _d, _e;
            if (self.isOnboarding && !!self.showDate) {
                self.isRevealed = true;
                self.isPressShow = true;
            }
            else if (self.acquireStatus === ACQUIRE_STATUS.none) {
                self.isLoading = true;
                const acquiredContactsResult = yield acquireContacts({
                    requestId: self.requestId,
                    platform: BULK_SEARCH_PLATFORM,
                    contacts: [self.contactHash],
                });
                if (acquiredContactsResult === null || acquiredContactsResult === void 0 ? void 0 : acquiredContactsResult.length) {
                    self.setAcquireStatus(ACQUIRE_STATUS.pending);
                }
                self.isLoading = false;
            }
            else {
                self.isLoading = true;
                try {
                    const { showResult, response, numberOfCreditsCharged } = yield self.revealContact();
                    if (((_a = envConfig.FEATURES) === null || _a === void 0 ? void 0 : _a.NOTIFY_EMAIL_ON_DEMAND) &&
                        (self.isFromDetailsFoundTab || self.isOnDemandContactFound)) {
                        sendClientMetric(buildOnDemandShowClientMetricPayload(false, self.isFromDetailsFoundTab, self.isOnDemandContactFound));
                    }
                    if (response && ((_b = showResult === null || showResult === void 0 ? void 0 : showResult.data) === null || _b === void 0 ? void 0 : _b.type) === 'success') {
                        self.setContact(Object.assign(Object.assign({}, response), { isFreshData: self.isFreshData, freshDataSummary: self.freshDataSummary, previousJob: self.previousJob, isFromDetailsFoundTab: self.isFromDetailsFoundTab, isOnDemandContactFound: self.isOnDemandContactFound }));
                        self.isRevealed = true;
                        self.isPressShow = true;
                        if (!self.isOnboarding && ((_c = showResult.enterprise295Consumption) === null || _c === void 0 ? void 0 : _c.regular) > 0) {
                            getRoot(self).user.onCreditUsed();
                        }
                        const root = getRoot(self);
                        if (root.userActivity) {
                            root.userActivity.checkAndShowRateModal(RATE_MODAL_TRIGGER_ACTIONS.CONTACT_REVEAL);
                            root.userActivity.reportContactsRevealed({
                                action: RATE_MODAL_TRIGGER_ACTIONS.CONTACT_REVEAL,
                                contacts: [(_d = response === null || response === void 0 ? void 0 : response.contact) === null || _d === void 0 ? void 0 : _d.id],
                            });
                        }
                        if (root.fetchAccountCredits) {
                            root.fetchAccountCredits(numberOfCreditsCharged);
                        }
                    }
                    self.isLoading = false;
                    showResult.numberOfCreditsCharged = numberOfCreditsCharged;
                    return showResult;
                }
                catch (e) {
                    self.isLoading = false;
                    // for some reason, bulk error object gets here as a string, with no data
                    if (self.isFromDetailsFoundTab && e === OUT_OF_CREDITS_BULK_ERROR_MESSAGE) {
                        return OUT_OF_CREDITS_BULK_ERROR_OBJECT;
                    }
                    return { errorCode: (_e = e.statusCode) !== null && _e !== void 0 ? _e : e.code, message: e.message, data: e === null || e === void 0 ? void 0 : e.data };
                }
            }
            return self;
        }),
        saveToWishlist: flow(function* saveToWishlist({ contactCompany, contactFirstName, contactLastName, contactTitle, contactEmail, contactPhone, listIdToSave = '', }) {
            const { context, userSettings } = getRoot(self);
            const { url, parserResult } = context;
            const { platform } = userSettings;
            const { parsedFullName, parsedCompanyName, parsedLinkedinUrl } = getParsedData(parserResult, self);
            const data = getContactWishlistData({
                contactCompany,
                contactFirstName,
                contactLastName,
                contactTitle,
                contactEmail,
                contactPhone,
                parsedDataFullName: parsedFullName,
                parsedDataCompany: parsedCompanyName,
                parsedLinkedinUrl,
                url,
                platform: SAVE_CONTACT_SUB_PLATFORMS[platform] || SUB_PLATFORM.LINKEDIN_MAIN,
            });
            try {
                const response = yield saveContactToWishlist(data);
                self.setWishlistContact(Object.assign(Object.assign({}, response === null || response === void 0 ? void 0 : response.wishedContacts[0]), { listId: listIdToSave !== null && listIdToSave !== void 0 ? listIdToSave : '' }));
                return response;
            }
            catch (error) {
                return null;
            }
        }),
        undoSave: () => {
            self.setContactList = '';
            self.integrationsUrl.clear();
        },
        setContactList: newList => {
            self.contactList = newList;
            if (self.isWishlistContact) {
                self.wishlistData.lists = [...self.wishlistData.lists, newList];
            }
        },
        setContactIntegrationData: (integrationKey, data) => {
            self.setIntegrationByKey(integrationKey, data);
            if (self.isWishlistContact) {
                self.wishlistData.integrations.set(integrationKey, { link: data === null || data === void 0 ? void 0 : data.link });
            }
        },
        setFeedback: (key, feedbackValue) => {
            self[key].feedback = feedbackValue;
        },
        setIsPressShow: value => {
            self.isPressShow = value;
        },
        setIsChecked: value => {
            self.isChecked = value;
        },
        setSearchStatus: value => {
            self.searchStatus = value;
        },
        setAcquireStatus: value => {
            self.acquireStatus = value;
        },
        onTagsChanged: ({ tags }) => {
            self.tags = [...tags];
        },
        setUniqueSearchId: uniqueSearchId => {
            self.uniqueSearchId = uniqueSearchId;
        },
        setSequenceNum: sequenceNum => {
            self.sequenceNum = sequenceNum;
        },
        setShowDataPointType: type => {
            self.selectedDataPointType = type;
        },
        setCreditSplitButtonId: id => {
            self.creditSplitClickedButtonId = id;
        },
        setShowCreditSplitPopup: (isOpen, type) => {
            self.showCreditSplitPopup = isOpen;
            self.creditSplitErrorType = type;
        },
        init: type => {
            if (type === 'UNKNOWN') {
                self.searchStatus = SEARCH_STATUS.IDLE;
            }
            else {
                resetState();
                searchTries = 0;
                // generate new request id only when url changed
                self.requestId = uuid();
            }
        },
    };
};
export default Actions;
